import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useMemberPaymentMethod,
  useMemberPaymentMethodAsMember,
} from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { PaymentMethodDTO, UserMemberBean } from "@gymflow/types";
import { useContext } from "react";

import { ApiResolverContext } from "../../../providers/ApiResolverProvider/ApiResolverContext";
import useGymflowModels from "../../../store";
import { Button, TrashIcon } from "../../atoms";
import { StarIcon } from "../../atoms/icons/StarIcon";
import { PaymentMethodDetails } from "../../molecules/PaymentMethodDetails";

export const PaymentMethodList = ({
  userMember,
  paymentMethods,
  asMember,
  className
}: {
  userMember: UserMemberBean;
  paymentMethods?: PaymentMethodDTO[];
  asMember?: boolean;
  className?: string;
}) => {
  const { api } = useGymflowModels();
  const { clubId } = useContext(ApiResolverContext);
  const paymentMethodMutationsAsStaff = useMemberPaymentMethod({
    api,
  });

  const paymentMethodMutationsAsMember = useMemberPaymentMethodAsMember({
    api,
  });
  const { removePaymentMethodMutation, assignDefaultPaymentMethodMutation } =
    asMember ? paymentMethodMutationsAsMember : paymentMethodMutationsAsStaff;

  const [parent] = useAutoAnimate();
  if (!paymentMethods) return undefined;

  return (
    <div
      ref={parent}
      className={cn("absolute inset-0 -mx-8  flex flex-col gap-2 overflow-y-auto px-8", className)}
    >
      {paymentMethods
        .sort((a, b) =>
          a.defaultPaymentMethod
            ? -1
            : b.defaultPaymentMethod
              ? 1
              : a.paymentMethodId.localeCompare(b.paymentMethodId),
        )
        .map((paymentMethod) => {
          return (
            <div
              className="flex w-full flex-row items-center gap-2"
              key={paymentMethod.paymentMethodId}
            >
              <div className="bg-gray-0 flex h-fit max-h-fit flex-1 flex-row items-center justify-between gap-2 rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-800 dark:bg-gray-950">
                <PaymentMethodDetails
                  paymentMethod={paymentMethod}
                  userMemberId={userMember.id}
                  as={asMember ? "member" : "staff"}
                />
                <Button
                  disabled={paymentMethod.status !== "ACTIVE"}
                  className="h-fit min-h-0 min-w-0 p-2"
                  intent="transparent"
                  onClick={async () => {
                    await assignDefaultPaymentMethodMutation.mutateAsync({
                      memberId: userMember.id,
                      paymentMethodId: paymentMethod.paymentMethodId,
                      clubId: clubId,
                    });
                  }}
                  tooltip="Set as default"
                >
                  <StarIcon
                    className="size-5"
                    pathClassName={cn({
                      "fill-secondary-500 stroke-secondary-500":
                        paymentMethod.defaultPaymentMethod,
                      "fill-transparent stroke-gray-500":
                        !paymentMethod.defaultPaymentMethod,
                    })}
                  />
                </Button>
              </div>
              <Button
                className={cn(
                  "flex h-10 max-h-none w-10 items-center justify-center p-0",
                  {
                    invisible: asMember
                      ? paymentMethod.defaultPaymentMethod
                      : false,
                  },
                )}
                intent="error-outline"
                onClick={async () => {
                  await removePaymentMethodMutation.mutateAsync({
                    clubId: clubId,
                    memberId: userMember.id,
                    paymentMethodId: paymentMethod.paymentMethodId,
                  });
                }}
              >
                <TrashIcon
                  pathClassName="stroke-error-500"
                  className="size-5"
                />
              </Button>
            </div>
          );
        })}
    </div>
  );
};
